/**
 * Tipos de las tareas, compartidos por servidor y cliente.
 *
 * Una tarea es como en un aula virtual: quien la crea deja instrucciones (y,
 * si quiere, un archivo con el enunciado) y cada estudiante entrega su trabajo
 * en PDF o Word. Se entra con un PIN, sin cuenta.
 *
 * Una tarea puede ser de un curso. Si el curso tiene lista de estudiantes,
 * cada uno elige su nombre al entrar y puede tener su propio material (su
 * práctica personal) para descargar.
 */

/** Tope de cada archivo: enunciado, material personal y entrega. */
export const TASK_MAX_FILE_MB = 100;

/** Lo que se acepta subir, para el <input type="file">. */
export const TASK_FILE_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export interface FileInfo {
  name: string;
  size: number;
}

export interface CourseStudent {
  id: string;
  name: string;
}

export interface Course {
  id: string;
  name: string;
  students: CourseStudent[];
  createdAt: string;
}

/** Lo que ve quien crea la tarea en su lista. */
export interface TaskSummary {
  id: string;
  code: string;
  title: string;
  instructions: string;
  courseId: string | null;
  courseName: string | null;
  /** Fecha de entrega (ms), o null si no tiene. */
  dueAt: number | null;
  /** Pasada la fecha, ¿se puede seguir entregando (marcado «con retraso»)? */
  acceptLate: boolean;
  gradeScale: number;
  isOpen: boolean;
  attachmentName: string | null;
  attachmentSize: number | null;
  createdAt: string;
  /** Entregas con archivo. */
  submitted: number;
  graded: number;
}

export interface TaskSubmissionSummary {
  id: string;
  /** Estudiante de la lista del curso, si entró eligiendo su nombre. */
  studentId: string | null;
  firstName: string;
  lastName: string;
  hasAccount: boolean;
  fileName: string | null;
  fileSize: number | null;
  note: string;
  submittedAt: number | null;
  late: boolean;
  grade: number | null;
  feedback: string;
  gradedAt: number | null;
}

/** Una fila por estudiante del curso: su material y su entrega. */
export interface TaskRosterRow {
  student: CourseStudent;
  material: FileInfo | null;
  /** Primera vez que descargó su práctica (ms), o null si nunca. */
  downloadedAt: number | null;
  downloads: number;
  submission: TaskSubmissionSummary | null;
}

export interface TaskDetail {
  task: TaskSummary;
  /** Con curso con lista: una fila por estudiante. Si no, vacío. */
  roster: TaskRosterRow[];
  /** Entregas que no son de nadie de la lista (sin curso, o curso sin lista). */
  submissions: TaskSubmissionSummary[];
}

/** Ficha que se enseña al meter el PIN, antes de poner el nombre. */
export interface TaskPublicInfo {
  code: string;
  title: string;
  instructions: string;
  creatorName: string;
  courseName: string | null;
  /** Con lista, quien entra elige su nombre de aquí en vez de escribirlo. */
  students: CourseStudent[] | null;
  dueAt: number | null;
  acceptLate: boolean;
  isOpen: boolean;
  attachmentName: string | null;
  attachmentSize: number | null;
}

/** Lo que ve cada estudiante de su propia entrega. */
export interface TaskSubmissionState {
  submissionId: string;
  task: TaskPublicInfo;
  firstName: string;
  lastName: string;
  /** Su práctica personal, si quien creó la tarea le dejó una. */
  material: FileInfo | null;
  fileName: string | null;
  fileSize: number | null;
  note: string;
  submittedAt: number | null;
  late: boolean;
  /** Se puede subir o cambiar el archivo ahora mismo. */
  canSubmit: boolean;
  /** Por qué no, si no se puede. */
  blockedReason: string | null;
  grade: number | null;
  gradeScale: number;
  feedback: string;
  serverNow: number;
}
